import React, { useState, useEffect, useMemo } from 'react';
import { DataTable } from '../common/DataTable';
import { Cliente } from '../../types';
import { FirebaseService } from '../../services/firebaseService';
import { useNavigate } from 'react-router-dom';
import {
  Phone, MapPin, Clock, Plus, Package, Beer,
  Edit, Trash2, History, PhoneCall, MessageSquare, Box,
  AlertCircle, FileText, Users
} from 'lucide-react';
import toast from 'react-hot-toast';
import { ClientesFilters } from './ClientesFilters';
import { LoadingSpinner } from '../common/LoadingSpinner';

export const ClientesList: React.FC = () => {
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [observacionesModalOpen, setObservacionesModalOpen] = useState(false);
  const [observacionesCliente, setObservacionesCliente] = useState<{nombre: string, observaciones: string} | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [clienteToDelete, setClienteToDelete] = useState<Cliente | null>(null);
  const navigate = useNavigate();
  const [filters, setFilters] = useState<ClientesFilters>({
    searchTerm: '',
    diaVisita: '',
    frecuenciaVisita: '',
    mostrarDeudores: false,
    mostrarEntregasRecientes: false,
  });

  const loadClientes = async () => {
    setLoading(true);
    try {
      const data = await FirebaseService.getCollection<Cliente>('clientes');
      setClientes(data);
    } catch (error) {
      console.error('Error al cargar clientes:', error);
      toast.error('Error al cargar los clientes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClientes();

    // Recargar cuando la ventana recupera el foco
    const onFocus = () => loadClientes();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  // Filtrar clientes basado en los criterios
  const clientesFiltrados = useMemo(() => {
    // Función para normalizar texto (quitar acentos y convertir a minúsculas)
    const normalizeText = (text: string) => {
      return text
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
    };

    // Función para calcular la similitud entre dos strings (tolerante a errores)
    const calculateSimilarity = (str1: string, str2: string) => {
      const norm1 = normalizeText(str1);
      const norm2 = normalizeText(str2);
      
      // Si uno contiene al otro, es una coincidencia
      if (norm1.includes(norm2) || norm2.includes(norm1)) {
        return true;
      }
      
      // Verificar si las palabras coinciden parcialmente
      const words1 = norm1.split(' ');
      const words2 = norm2.split(' ');
      
      return words1.some(word1 => 
        words2.some(word2 => 
          word1.length > 3 && word2.length > 3 && 
          (word1.includes(word2) || word2.includes(word1))
        )
      );
    };

    const esClienteDeHoy = (cliente: Cliente) => {
      const today = new Date();
      const dayOfWeek = today.getDay();
      const dayOfMonth = today.getDate();
      const weekOfYear = Math.ceil(dayOfMonth / 7);
      const daysMap: Record<number, string> = {
        0: 'domingo', 1: 'lunes', 2: 'martes', 3: 'miércoles',
        4: 'jueves', 5: 'viernes', 6: 'sábado'
      };
      const todayName = daysMap[dayOfWeek];
      
      const diasVisita = cliente.diasVisita || (cliente.diaVisita ? [cliente.diaVisita] : []);
      const matchesDay = diasVisita.includes(todayName as any);
      if (!matchesDay) return false;
      
      switch (cliente.frecuenciaVisita) {
        case 'semanal': return true;
        case 'quincenal': return weekOfYear % 2 === 1;
        case 'mensual': return dayOfMonth <= 7;
        default: return false;
      }
    };

    const filtered = clientes.filter(cliente => {
      // 1. Filtro por texto (nombre, dirección, teléfono)
      if (filters.searchTerm) {
        const searchTerm = filters.searchTerm.toLowerCase();
        const matchNombre = calculateSimilarity(cliente.nombre, searchTerm);
        const matchDireccion = calculateSimilarity(cliente.direccion, searchTerm);
        const matchTelefono = cliente.telefono.includes(searchTerm);
        
        if (!matchNombre && !matchDireccion && !matchTelefono) {
          return false;
        }
      }

      // 2. Filtro por día de visita
      if (filters.diaVisita && cliente.diaVisita !== filters.diaVisita) {
        return false;
      }

      // 3. Filtro por frecuencia
      if (filters.frecuenciaVisita && cliente.frecuenciaVisita !== filters.frecuenciaVisita) {
        return false;
      }

      // 4. Filtro de deudores
      if (filters.mostrarDeudores) {
        const saldoPendiente = cliente.saldoPendiente ?? 0;
        if (saldoPendiente <= 0) {
          return false;
        }
      }

      // 5. Filtro de entregas recientes (últimos 7 días)
      if (filters.mostrarEntregasRecientes) {
        if (!cliente.ultimaEntregaFecha) {
          return false;
        }
        const fechaLimite = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000); // 7 días atrás
        if (new Date(cliente.ultimaEntregaFecha) < fechaLimite) {
          return false;
        }
      }

      return true;
    });

    return filtered.sort((a, b) => {
      const aEsHoy = esClienteDeHoy(a);
      const bEsHoy = esClienteDeHoy(b);
      if (aEsHoy && !bEsHoy) return -1;
      if (!aEsHoy && bEsHoy) return 1;
      return a.nombre.localeCompare(b.nombre);
    });
  }, [clientes, filters]);



  const handleDeleteCliente = (cliente: Cliente) => {
    setClienteToDelete(cliente);
    setDeleteModalOpen(true);
  };

  const confirmDeleteCliente = async () => {
    if (!clienteToDelete?.id) return;
    
    try {
      await FirebaseService.deleteDocument('clientes', clienteToDelete.id);
      toast.success('Cliente eliminado correctamente');
      loadClientes();
      setDeleteModalOpen(false);
      setClienteToDelete(null);
    } catch (error) {
      console.error('Error al eliminar cliente:', error);
      toast.error('Error al eliminar cliente');
    }
  };

  const cancelDeleteCliente = () => {
    setDeleteModalOpen(false);
    setClienteToDelete(null);
  };

  const handleLlamar = (cliente: Cliente) => {
    if (cliente.telefono) {
      window.open(`tel:${cliente.telefono}`, '_blank');
    }
  };

  const handleWhatsApp = (cliente: Cliente) => {
    if (cliente.telefono) {
      const mensaje = encodeURIComponent('Hola, te contacto desde SodAsist');
      window.open(`https://wa.me/${cliente.telefono}?text=${mensaje}`, '_blank');
    }
  };

  const handleVerHistorial = (cliente: Cliente) => {
    if (cliente.id) {
      // Navegar a la lista de entregas con filtro para este cliente específico
      navigate(`/entregas?clienteId=${cliente.id}`);
    }
  };

  const handleVerObservaciones = async (cliente: Cliente) => {
    if (!cliente.id) return;
    
    try {
      const clienteCompleto = await FirebaseService.getDocument<Cliente>('clientes', cliente.id);
      if (clienteCompleto && clienteCompleto.observaciones) {
        setObservacionesCliente({
          nombre: clienteCompleto.nombre,
          observaciones: clienteCompleto.observaciones
        });
        setObservacionesModalOpen(true);
      } else {
        toast.error('Este cliente no tiene observaciones registradas');
      }
    } catch (error) {
      console.error('Error al cargar observaciones:', error);
      toast.error('Error al cargar las observaciones');
    }
  };

  // Acciones principales (siempre visibles)
  const primaryActions = [
    {
      label: 'Editar',
      icon: <Edit className="h-4 w-4" />,
      onClick: (cliente: Cliente) => navigate(`${cliente.id}`),
      tooltip: 'Editar datos del cliente',
      variant: 'primary' as const,
    },
    {
      label: 'Nueva Entrega',
      icon: <Box className="h-4 w-4" />,
      onClick: (cliente: Cliente) => navigate(`/entregas/nuevo?clienteId=${cliente.id}`),
      tooltip: 'Registrar nueva entrega',
      variant: 'success' as const,
    },
  ];

  // Acciones secundarias (en menú desplegable)
  const secondaryActions = [
    {
      label: 'Ver Historial',
      icon: <History className="h-4 w-4" />,
      onClick: handleVerHistorial,
      tooltip: 'Ver historial de entregas',
    },
    {
      label: 'Llamar',
      icon: <PhoneCall className="h-4 w-4" />,
      onClick: handleLlamar,
      show: () => 'ontouchstart' in window,
      tooltip: 'Llamar al cliente',
    },
    {
      label: 'WhatsApp',
      icon: <MessageSquare className="h-4 w-4" />,
      onClick: handleWhatsApp,
      tooltip: 'Enviar mensaje por WhatsApp',
    },
    {
      label: 'Eliminar',
      icon: <Trash2 className="h-4 w-4" />,
      onClick: handleDeleteCliente,
      className: 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10',
      tooltip: 'Eliminar cliente',
      variant: 'danger' as const,
    },
  ];

  // Combinar acciones para el DataTable
  const actions = [...primaryActions, ...secondaryActions];

  const columns = [
    /* 1 ─ Cliente completo (Nombre + Teléfono + Estado) */
    { 
      key: 'cliente' as keyof Cliente, 
      label: 'Cliente', 
      sortable: false,
      width: '30%',
      priority: 1,
      render: (_: unknown, row: Cliente) => {
        const saldoPendiente = row.saldoPendiente ?? 0;
        return (
          <div className="space-y-1">
            <div className="flex items-center space-x-2">
              <span className="font-medium text-gray-900 dark:text-white">{row.nombre}</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleVerObservaciones(row);
                }}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                title="Ver observaciones"
              >
                <FileText className="h-4 w-4 text-blue-500 dark:text-blue-400" />
              </button>
              {saldoPendiente > 0 && (
                <div className="group relative inline-block">
                  <AlertCircle className="h-4 w-4 text-red-500 dark:text-red-400 cursor-help" />
                  <div className="hidden group-hover:block absolute z-[1000] w-48 p-3 bg-red-600 dark:bg-red-500 text-white text-sm rounded-lg shadow-lg -translate-x-1/2 left-1/2 mt-2">
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 border-8 border-transparent border-b-red-600 dark:border-b-red-500"></div>
                    <div>Saldo pendiente:</div>
                    <div className="font-semibold">
                      {new Intl.NumberFormat('es-AR', {
                        style: 'currency',
                        currency: 'ARS'
                      }).format(saldoPendiente)}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
              <Phone className="h-3 w-3" />
              <span>{row.telefono}</span>
            </div>
          </div>
        );
      },
    },
    /* 2 ─ Dirección */
    { 
      key: 'direccion' as keyof Cliente, 
      label: 'Dirección',
      width: '25%',
      priority: 2,
      render: (value: unknown) => (
        <div className="flex items-center space-x-2">
          <MapPin className="h-4 w-4 text-gray-400 dark:text-gray-300" />
          <span className="truncate text-gray-900 dark:text-white">{String(value)}</span>
        </div>
      ),
    },
    /* 3 ─ Visita y Frecuencia */
    { 
      key: 'visita' as keyof Cliente, 
      label: 'Visita',
      width: '15%',
      hideOnMobile: true,
      render: (_: unknown, row: Cliente) => (
        <div className="space-y-1">
          <div className="flex items-center space-x-2">
            <Clock className="h-4 w-4 text-gray-400 dark:text-gray-300" />
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {String(row.diaVisita).charAt(0).toUpperCase() + String(row.diaVisita).slice(1)}
            </span>
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 capitalize">
            {row.frecuenciaVisita}
          </div>
        </div>
      ),
    },
    /* 4 ─ Consumo */
    { 
      key: 'consumo' as keyof Cliente, 
      label: 'Consumo',
      width: '15%',
      hideOnMobile: true,
      render: (_: unknown, row: Cliente) => (
        <div className="space-y-1">
          <div className="flex items-center space-x-1">
            <Package className="h-4 w-4 text-gray-400 dark:text-gray-300" />
            <span className="text-sm text-gray-900 dark:text-white">{row.consumoPromedioBidones || 0} bidones</span>
          </div>
          <div className="flex items-center space-x-1">
            <Beer className="h-4 w-4 text-gray-400 dark:text-gray-300" />
            <span className="text-sm text-gray-900 dark:text-white">{row.consumoPromedioSodas || 0} sodas</span>
          </div>
        </div>
      ),
    },
    /* 5 ─ Estado */
    { 
      key: 'estado' as keyof Cliente, 
      label: 'Estado',
      width: '15%',
      hideOnMobile: true,
      render: (_: unknown, row: Cliente) => {
        const saldoPendiente = row.saldoPendiente ?? 0;
        const sinEntregas = !row.ultimaEntregaFecha;
        
        if (saldoPendiente > 0) {
          return (
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
              <span className="text-xs text-red-600 dark:text-red-400 font-medium">Con deuda</span>
            </div>
          );
        }
        
        if (sinEntregas) {
          return (
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
              <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">Sin entregas</span>
            </div>
          );
        }
        
        return (
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-xs text-green-600 dark:text-green-400 font-medium">Activo</span>
          </div>
        );
      },
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }


  return (
    <div className="space-y-6">
      {/* Header optimizado para PC */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Título */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg">
                  <Users className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
                    Gestión de Clientes
                  </h1>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Administra tu base de clientes y seguimiento
                  </p>
                </div>
              </div>
            </div>

            {/* Acciones principales */}
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('new')}
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <Plus className="h-5 w-5 mr-2" />
                Nuevo Cliente
              </button>
            </div>
          </div>
        </div>
      </div>

      <ClientesFilters
        filters={filters}
        onFiltersChange={setFilters}
        className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-4"
      />

      <DataTable
        data={clientesFiltrados}
        columns={columns}
        actions={actions}
        searchable={false}
        onRowClick={(cliente: Cliente) => cliente.id && navigate(`${cliente.id}`)}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100]
        }}
        className="overflow-hidden"
      />

      {/* Modal de observaciones */}
      {observacionesModalOpen && observacionesCliente && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Observaciones de {observacionesCliente.nombre}
              </h3>
              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                {observacionesCliente.observaciones}
              </p>
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setObservacionesModalOpen(false)}
                  className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 dark:focus:ring-gray-400"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      {deleteModalOpen && clienteToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                    <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Eliminar Cliente
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Esta acción no se puede deshacer
                  </p>
                </div>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-700 dark:text-gray-300">
                  ¿Estás seguro de que quieres eliminar a{' '}
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {clienteToDelete.nombre}
                  </span>?
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  Se eliminarán todos los datos asociados a este cliente, incluyendo su historial de entregas.
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={cancelDeleteCliente}
                  className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 dark:focus:ring-gray-400 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmDeleteCliente}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                >
                  Eliminar Cliente
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};